import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG } from '@boma/app-config';
import {
  ICartItem,
  ICheckout,
  PaymentType,
} from '@boma/website/shared/entities';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly _base: string;
  private readonly _version: string;

  private _cartItems: ICartItem[] = [];

  data$ = new BehaviorSubject<{ items: ICartItem[]; count: number }>(null);

  constructor(
    @Inject(APP_CONFIG) private configuration: any,
    private httpClient: HttpClient
  ) {
    this._base = configuration.apiRoute;
    this._version = configuration.apiVersion;
  }

  private _notifyListeners() {
    this.data$.next({
      items: this._cartItems,
      count: this._cartItems.length,
    });
  }

  async addItemToCart(
    entityId: string,
    locationId: string,
    supplierEntityLocationId: string,
    productId: string,
    quantity: number,
    paymentType: string | PaymentType
  ) {
    const cartItem = await firstValueFrom(
      this._addCartItem(
        entityId,
        locationId,
        supplierEntityLocationId,
        productId,
        quantity,
        paymentType
      )
    );

    this._addOrUpdateCartItemLocally(cartItem);
    this._notifyListeners();
    return cartItem;
  }

  async loadCartItems(
    entityId: string,
    locationId: string
  ): Promise<ICartItem[]> {
    this._cartItems = [];
    this._notifyListeners();
    this._cartItems = await firstValueFrom(
      this._loadCartItems(entityId, locationId)
    );
    this._notifyListeners();
    return this._cartItems;
  }

  async deleteCartItem(
    entityId: string,
    locationId: string,
    supplierLocationId: string,
    productId: string
  ) {
    await firstValueFrom(
      this._deleteCartItem(entityId, locationId, supplierLocationId, productId)
    );

    const index = this._getCartItemIndexInLocalCollection(
      supplierLocationId,
      productId
    );
    this._cartItems.splice(index, 1);
    this._notifyListeners();
  }

  async checkout(entityId: string, locationId: string): Promise<ICheckout> {
    const checkoutResult = await firstValueFrom(
      this._checkout(entityId, locationId)
    );
    this._cartItems = [];
    this._notifyListeners();
    return checkoutResult;
  }

  private _addOrUpdateCartItemLocally(cartItem: ICartItem) {
    const index = this._getCartItemIndexInLocalCollection(
      cartItem.entity.locationId,
      cartItem.product.id
    );
    if (index === -1) {
      this._cartItems.push(cartItem);
    } else {
      this._cartItems[index] = cartItem;
    }
  }

  private _getCartItemIndexInLocalCollection(
    supplierLocationId: string,
    productId: string
  ): number {
    return this._cartItems.findIndex(
      (ci) =>
        ci.entity.locationId === supplierLocationId &&
        ci.entity.productId === productId
    );
  }

  private _addCartItem(
    entityId: string,
    locationId: string,
    supplierEntityLocationId: string,
    productId: string,
    quantity: number,
    paymentType: string | PaymentType
  ) {
    return this.httpClient.post<ICartItem>(
      `${this._base}api/v${this._version}/${entityId}/Cart/${locationId}`,
      {
        supplierEntityLocationId,
        productId,
        quantity,
        paymentType,
      }
    );
  }

  private _loadCartItems(entityId: string, locationId: string) {
    return this.httpClient.get<ICartItem[]>(
      `${this._base}api/v${this._version}/${entityId}/Cart/${locationId}`
    );
  }

  private _checkout(entityId: string, locationId: string) {
    return this.httpClient.post<ICheckout>(
      `${this._base}api/v${this._version}/${entityId}/Cart/${locationId}/Checkout`,
      {}
    );
  }

  private _deleteCartItem(
    entityId: string,
    locationId: string,
    supplierLocationId: string,
    productId: string
  ) {
    return this.httpClient.delete(
      `${this._base}api/v${this._version}/${entityId}/Cart/${locationId}/${supplierLocationId}/${productId}`
    );
  }
}
